/** A single wind observation, normalized across all upstream sources. */
export interface Reading {
  /** Observation time. Always absolute — sources are parsed as UTC. */
  t: Date;
  /** Sustained wind speed in knots. */
  speedKt: number;
  /** Direction the wind is coming FROM, in degrees true. */
  dirDeg: number | null;
  /** Gust in knots, when the station reports one. */
  gustKt: number | null;
}

/** A single scalar sample. Non-wind series keep their own cadence and gaps. */
export interface Point {
  t: Date;
  v: number;
}

/**
 * Which sensors a station actually carries, probed against the live API rather
 * than assumed. Coverage is genuinely patchy — water level exists at 6 of the 14
 * CO-OPS stations and water temperature at 3, buoy 46013 reports no water
 * temperature and 46026 no air temperature — so panels are built from this
 * rather than from hopeful requests that mostly 404.
 */
export interface Sensors {
  waterLevel?: boolean;
  waterTemp?: boolean;
  airTemp?: boolean;
  waves?: boolean;
}

export interface Station {
  id: string;
  name: string;
  lat: number;
  lon: number;
  /**
   * `konect` is the Golden Gate Bridge district's own logger, published through
   * a Campbell Scientific kiosk rather than a government feed.
   */
  source: 'coops' | 'ndbc' | 'konect';
  /** A moored buoy, or a pier/shore-mounted sensor. */
  kind: 'buoy' | 'shore';
  sensors: Sensors;
  /**
   * Minutes between transmissions, where the station differs from its source's
   * norm. Drives how old a reading may be before it reads as stale.
   */
  reportMinutes?: number;
}

/**
 * Everything plotted for one station. Each series is independent — CO-OPS serves
 * them as separate products on their own cadences, so they are never assumed to
 * share timestamps with the wind.
 */
export interface StationHistory {
  wind: Reading[];
  waterLevel?: Point[];
  /** Predicted tide over the same window, for comparison against observed. */
  waterLevelPredicted?: Point[];
  waterTemp?: Point[];
  airTemp?: Point[];
  waveHeight?: Point[];
}

/** Per-station live state held by the app. */
export interface StationState {
  station: Station;
  latest: Reading | null;
  /** Populated lazily when a popup opens. */
  history: StationHistory | null;
  /** Set when the most recent fetch failed or returned no usable data. */
  error: string | null;
  loading: boolean;
}

export const MS_PER_KT = 1.94384;

/** How long each feed takes to publish a reading after it is taken. */
const PUBLISH_LAG_MIN: Record<Station['source'], number> = {
  coops: 20,
  ndbc: 60,
  // The kiosk shows a row within a minute of the logger writing it.
  konect: 5,
};

/** Default reporting interval where a station does not declare its own. */
const DEFAULT_REPORT_MIN: Record<Station['source'], number> = {
  coops: 6,
  ndbc: 10,
  konect: 5,
};

/**
 * How old a reading may be before it stops counting as current.
 *
 * Derived from the station's own reporting interval rather than a flat
 * per-source constant. Cadence is not uniform even within a source: buoy 46012
 * transmits hourly while 46026, 46013 and 46042 transmit every ten minutes, so a
 * single NDBC threshold either marked healthy hourly data dead — 46012 showed as
 * a grey no-data ring most of the time — or was loose enough to hide a genuine
 * outage on the fast buoys.
 *
 * Two missed transmissions plus the publication lag is the allowance.
 */
export function staleAfterMs(station: Station): number {
  const report = station.reportMinutes ?? DEFAULT_REPORT_MIN[station.source];
  return (report * 2 + PUBLISH_LAG_MIN[station.source]) * 60_000;
}

/**
 * What a national NDBC station claims as its reporting interval.
 *
 * realtime2 publishes every ten minutes, which is what the panel reads and what
 * the map's per-station refresh fetches. Exported so the marker and the panel
 * cannot drift apart: they previously used 80 minutes and 100 minutes
 * respectively, leaving a window where clicking a marker that looked current
 * reported it as stale.
 */
export const NATIONAL_REPORT_MIN = 10;

/** The one staleness window for a national station, in whole minutes. */
export function nationalStaleMinutes(): number {
  return NATIONAL_REPORT_MIN * 2 + PUBLISH_LAG_MIN.ndbc;
}

export function isStale(station: Station, t: Date, now: Date = new Date()): boolean {
  return now.getTime() - t.getTime() > staleAfterMs(station);
}

/** True when a station has a reading recent enough to plot as current. */
export function isCurrent(state: StationState, now: Date = new Date()): boolean {
  return state.latest !== null && !isStale(state.station, state.latest.t, now);
}

export function knotsFromMs(ms: number): number {
  return ms * MS_PER_KT;
}

/** Compass point for a bearing, e.g. 231 -> "SW". */
export function cardinal(deg: number): string {
  const points = [
    'N', 'NNE', 'NE', 'ENE', 'E', 'ESE', 'SE', 'SSE',
    'S', 'SSW', 'SW', 'WSW', 'W', 'WNW', 'NW', 'NNW',
  ];
  return points[Math.round((((deg % 360) + 360) % 360) / 22.5) % 16];
}
